/*
  Where this deployment's identity provider and API live, resolved at RUN time.

  WHY NOT JUST THE VITE ENV VARS

  `import.meta.env.VITE_*` is substituted into the bundle at build time. That is fine
  while there is one deployment, and it stops being fine the moment Tracer runs in a
  client's own tenant: their Keycloak is at their URL, in their realm, and a build-time
  constant means a separate `npm run build` and a separate artefact per customer. The
  thing that ships would no longer be the thing that was tested.

  So the values are fetched from `/config.json` — a small file that sits NEXT TO the
  bundle rather than inside it. One build, deployed anywhere; the tenant supplies the
  file. It is also the only way an operator can repoint a deployed SPA at a new realm
  without a rebuild, which is what happens the first time a customer migrates identity
  providers.

  PRECEDENCE IS PER FIELD, AND THAT IS DELIBERATE

  A non-empty value in `config.json` wins; anything empty or absent falls through to the
  build-time value. That is what lets the file ship inert — the copy in `public/` has
  empty strings in it — so local development keeps reading `.env.development` and nobody
  has to maintain two sources of the same truth to run `npm run dev`.

  NOTHING SECRET GOES IN HERE. It is a public file served to every browser. A realm name
  and a public client id are not secrets; a client secret would be, which is exactly why
  the SPA uses authorization-code + PKCE and has none.
*/

export interface RuntimeConfig {
  keycloak: {
    /** Base URL, no realm path. Empty runs against the API's development bypass. */
    url: string
    realm: string
    /** The public client. Also the client whose roles the API honours. */
    clientId: string
  }
  /** Absolute API origin. Empty means same-origin (the dev proxy, or a SWA rewrite). */
  apiBaseUrl: string
}

/** What the bundle was built with. The fallback for anything `config.json` leaves empty. */
const buildTime: RuntimeConfig = {
  keycloak: {
    url: import.meta.env.VITE_KEYCLOAK_URL ?? '',
    realm: import.meta.env.VITE_KEYCLOAK_REALM ?? 'tracer',
    clientId: import.meta.env.VITE_KEYCLOAK_CLIENT_ID ?? 'tracer-web',
  },
  apiBaseUrl: (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/+$/, ''),
}

let resolved: RuntimeConfig = buildTime

/**
 * Loads `/config.json` and merges it over the build-time values.
 *
 * Must be awaited before anything reads {@link runtimeConfig} — `main.tsx` does it first
 * thing, before Keycloak is initialised, because the Keycloak client cannot be
 * constructed without knowing its realm.
 *
 * A missing or unreadable file is not an error. The build-time values are a complete,
 * working configuration on their own, and a deployment that has not been given a
 * `config.json` is the normal case in development. Failing here would mean a typo in a
 * file that is allowed to be absent takes down the whole application.
 */
export async function loadRuntimeConfig(): Promise<RuntimeConfig> {
  try {
    /*
        no-store, not merely no-cache.

        This file is how an operator repoints a deployed SPA, so a stale copy is not a
        cosmetic problem — it is the application still talking to the previous identity
        provider after someone has been told the change is live. The CDN is told the same
        thing in staticwebapp.config.json; this is the half of it the browser obeys.
    */
    const response = await fetch('/config.json', { cache: 'no-store' })

    if (!response.ok) {
      return resolved
    }

    /*
        Verified to be JSON before parsing it.

        The SPA's navigation fallback rewrites unknown paths to index.html, so a
        misconfigured host answers this fetch with 200 and a page of HTML. `JSON.parse`
        then throws "Unexpected token '<'", which is a famously unhelpful thing to find
        in a console when the actual problem is a routing rule.
    */
    const contentType = response.headers.get('content-type') ?? ''

    if (!contentType.includes('json')) {
      console.warn(
        '/config.json did not come back as JSON. The host is probably serving index.html ' +
          'for it — check the navigation fallback excludes /config.json. Using the ' +
          'values built into the bundle.',
      )
      return resolved
    }

    const file = (await response.json()) as Partial<RuntimeConfig> | null

    resolved = {
      keycloak: {
        url: trimTrailingSlash(pick(file?.keycloak?.url, buildTime.keycloak.url)),
        realm: pick(file?.keycloak?.realm, buildTime.keycloak.realm),
        clientId: pick(file?.keycloak?.clientId, buildTime.keycloak.clientId),
      },
      apiBaseUrl: trimTrailingSlash(pick(file?.apiBaseUrl, buildTime.apiBaseUrl)),
    }
  } catch {
    // Network failure, malformed JSON, or no file at all. All three mean the same thing
    // here: use what the bundle was built with.
  }

  return resolved
}

/** The resolved configuration. Returns the build-time values until the load completes. */
export function runtimeConfig(): RuntimeConfig {
  return resolved
}

function pick(value: string | undefined | null, fallback: string): string {
  const trimmed = typeof value === 'string' ? value.trim() : ''
  return trimmed || fallback
}

/**
 * `${base}/api/periods` with a trailing slash on the base produces a double slash, which
 * some routers treat as a different path.
 */
function trimTrailingSlash(value: string): string {
  return value.replace(/\/+$/, '')
}
